import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Percent, Loader2, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminAdDiscounts — configure the "Rabat za dugoročni zakup" (long-term
 * ad-booking) discount tiers used by the public `/oglasavanje` CheckoutModal.
 * Default: -10% at 30 days, -20% at 90 days. Admin can add/edit tiers freely.
 */
export default function AdminAdDiscounts() {
  const { authFetch } = useAdmin();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    const r = await authFetch(`${API}/admin/ad-discount-tiers`);
    if (r.ok) { const d = await r.json(); setTiers(d.tiers || []); }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function update(idx, field, value) {
    setTiers(t => t.map((x, i) => i === idx ? { ...x, [field]: value } : x));
  }
  function addTier() {
    setTiers(t => [...t, { min_days: 7, discount_pct: 5, label: '' }]);
  }
  function removeTier(idx) {
    setTiers(t => t.filter((_, i) => i !== idx));
  }

  async function save() {
    setSaving(true);
    const cleaned = tiers
      .map(t => ({
        min_days: Math.max(1, parseInt(t.min_days) || 0),
        discount_pct: Math.max(0, Math.min(100, parseFloat(t.discount_pct) || 0)),
        label: (t.label || '').trim(),
      }))
      .filter(t => t.min_days > 0 && t.discount_pct > 0)
      .sort((a, b) => a.min_days - b.min_days);
    const r = await authFetch(`${API}/admin/ad-discount-tiers`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tiers: cleaned }),
    });
    if (r.ok) { toast.success('Spremljeno'); load(); } else toast.error('Greška');
    setSaving(false);
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-ad-discounts">
      <div className="flex items-center gap-3 mb-5">
        <Percent className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Rabat za dugoročni zakup</h1>
          <p className="text-sm text-zinc-400">Automatski popust u CheckoutModal-u kada je zakup oglasa ≥ zadani broj dana.</p>
        </div>
      </div>

      {loading ? (
        <Loader2 className="animate-spin text-zinc-500" />
      ) : (
        <>
          <div className="rounded-lg border border-zinc-800 overflow-hidden mb-4">
            <table className="w-full text-sm" data-testid="ad-discount-table">
              <thead className="bg-zinc-950 text-xs text-zinc-400">
                <tr>
                  <th className="text-left px-4 py-3">Minimalno dana</th>
                  <th className="text-left px-4 py-3">Popust (%)</th>
                  <th className="text-left px-4 py-3">Naljepnica (opcionalno)</th>
                  <th className="px-4 py-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t, i) => (
                  <tr key={i} className="border-t border-zinc-800">
                    <td className="px-3 py-2">
                      <input type="number" min={1} value={t.min_days}
                             onChange={e => update(i, 'min_days', e.target.value)}
                             className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm" data-testid={`tier-days-${i}`} />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" min={0} max={100} step={0.5} value={t.discount_pct}
                             onChange={e => update(i, 'discount_pct', e.target.value)}
                             className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm" data-testid={`tier-pct-${i}`} />
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" value={t.label || ''} placeholder="npr. Zakup ≥ 30 dana → -10%"
                             onChange={e => update(i, 'label', e.target.value)}
                             className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => removeTier(i)} className="text-red-400 hover:text-red-300" data-testid={`tier-remove-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {tiers.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500">Nema definiranih tierova.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2">
            <button onClick={addTier} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-sm rounded" data-testid="tier-add">
              <Plus size={14} /> Dodaj tier
            </button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded font-medium disabled:opacity-50" data-testid="tier-save">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Spremi
            </button>
          </div>
        </>
      )}
    </div>
  );
}
